import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Box, Flex, VStack } from '@chakra-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { LuCheck, LuClock, LuTriangleAlert, LuX, LuRefreshCw } from 'react-icons/lu'
import { Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { recepcionesService } from '../../api/services/recepciones.service'
import type {
  ValidacionConfigItem,
  ValidarItemResponse,
} from '../../api/models/recepcion.models'
import { ResultadoValidacion } from '../../api/models/recepcion.models'

const MotionBox = motion.create(Box)

interface ValidationState {
  config: ValidacionConfigItem
  status: 'pending' | 'running' | 'done'
  result: ValidarItemResponse | null
  error: string | null
}

const RESULT_ICON: Record<ResultadoValidacion, { icon: typeof LuCheck; color: string; tid: string }> = {
  [ResultadoValidacion.Conforme]: {
    icon: LuCheck,
    color: '#2a8a87',
    tid: 'recepciones.resultado.conforme',
  },
  [ResultadoValidacion.Observado]: {
    icon: LuTriangleAlert,
    color: '#EAB308',
    tid: 'recepciones.resultado.observado',
  },
  [ResultadoValidacion.NoConforme]: {
    icon: LuX,
    color: '#c8442f',
    tid: 'recepciones.resultado.noConforme',
  },
  [ResultadoValidacion.Pendiente]: {
    icon: LuClock,
    color: '#b5aa9d',
    tid: 'recepciones.resultado.pendiente',
  },
}

export default function ValidacionProgresoPage() {
  const { id: recepcionId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const colors = themeColors
  const { t } = useT()

  const validacionesConfig: ValidacionConfigItem[] =
    (location.state as { validaciones?: ValidacionConfigItem[] })?.validaciones ?? []

  const [validations, setValidations] = useState<ValidationState[]>(() =>
    validacionesConfig.map((config) => ({
      config,
      status: 'pending',
      result: null,
      error: null,
    })),
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const [allDone, setAllDone] = useState(false)
  const runningRef = useRef(false)

  const runValidation = useCallback(
    async (index: number) => {
      if (!recepcionId || index >= validations.length) {
        setAllDone(true)
        return
      }

      const config = validations[index].config

      setValidations((prev) =>
        prev.map((v, i) => (i === index ? { ...v, status: 'running' } : v)),
      )

      try {
        const result = await recepcionesService.validarItem(recepcionId, config.id)
        setValidations((prev) =>
          prev.map((v, i) => (i === index ? { ...v, status: 'done', result } : v)),
        )
      } catch (err) {
        setValidations((prev) =>
          prev.map((v, i) =>
            i === index
              ? { ...v, status: 'done', error: err instanceof Error ? err.message : 'Error' }
              : v,
          ),
        )
      }

      // Small delay before next
      await new Promise((r) => setTimeout(r, 600))
      setCurrentIndex(index + 1)
    },
    [recepcionId, validations.length],
  )

  useEffect(() => {
    if (runningRef.current) return
    if (currentIndex >= validations.length) {
      setAllDone(true)
      return
    }
    runningRef.current = true
    runValidation(currentIndex).finally(() => {
      runningRef.current = false
    })
  }, [currentIndex, runValidation, validations.length])

  // Redirect if no config
  useEffect(() => {
    if (validacionesConfig.length === 0) {
      navigate('/app/recepciones', { replace: true })
    }
  }, [validacionesConfig.length, navigate])

  const completedCount = validations.filter((v) => v.status === 'done').length
  const progress = validations.length > 0 ? (completedCount / validations.length) * 100 : 0

  const handleVerResultado = () => {
    navigate(`/app/recepciones/${recepcionId}/resultado`)
  }

  if (validacionesConfig.length === 0) return null

  return (
    <VStack gap={6} alignItems="stretch" maxW="700px" mx="auto" py={4}>
      {/* Header */}
      <MotionBox
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Text
          tid="recepciones.progreso.title"
          fontSize="xl"
          fontWeight="bold"
          color={colors.text}
          textAlign="center"
        />
        <Text
          tid="recepciones.progreso.subtitle"
          values={{ count: validations.length }}
          fontSize="sm"
          color={colors.textSecondary}
          textAlign="center"
          mt={1}
        />
      </MotionBox>

      {/* Progress bar */}
      <Box
        bg={colors.bgCard}
        borderRadius="2xl"
        p={5}
        border="1px solid"
        borderColor={colors.border}
      >
        <Flex justifyContent="space-between" mb={2}>
          <Text
            tid="recepciones.progreso.progressLabel"
            fontSize="xs"
            fontWeight="600"
            color={colors.textSecondary}
          />
          <Text
            tid="recepciones.progreso.progressCount"
            values={{ completed: completedCount, total: validations.length }}
            fontSize="xs"
            fontWeight="600"
            color={colors.textSecondary}
          />
        </Flex>
        <Box bg={colors.bgHover} borderRadius="full" height="8px" overflow="hidden">
          <motion.div
            style={{
              height: '100%',
              borderRadius: '9999px',
              background: brand.primary,
            }}
            initial={{ width: '0%' }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </Box>
      </Box>

      {/* Validation cards */}
      <VStack gap={3} alignItems="stretch">
        <AnimatePresence>
          {validations.map((validation, index) => {
            if (validation.status === 'pending') return null

            const isDone = validation.status === 'done'
            const resultInfo =
              isDone && validation.result
                ? RESULT_ICON[validation.result.resultado]
                : null
            const hasError = isDone && validation.error

            const IconComponent = isDone
              ? hasError
                ? LuX
                : resultInfo
                  ? resultInfo.icon
                  : LuCheck
              : LuRefreshCw

            const iconColor = isDone
              ? hasError
                ? '#c8442f'
                : resultInfo
                  ? resultInfo.color
                  : '#2a8a87'
              : colors.textMuted

            const badgeLabel = hasError
              ? t('recepciones.resultado.error')
              : validation.result
                ? t(RESULT_ICON[validation.result.resultado].tid)
                : ''

            return (
              <motion.div
                key={validation.config.id}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Box
                  bg={colors.bgCard}
                  border="1px solid"
                  borderColor={isDone ? `${iconColor}40` : colors.border}
                  borderRadius="xl"
                  p={4}
                >
                  <Flex alignItems="center" gap={3}>
                    <Flex
                      width="36px"
                      height="36px"
                      borderRadius="full"
                      bg={`${iconColor}15`}
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      {!isDone ? (
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                        >
                          <IconComponent size={18} color={iconColor} />
                        </motion.div>
                      ) : (
                        <IconComponent size={18} color={iconColor} />
                      )}
                    </Flex>

                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="600" color={colors.text}>
                        {validation.config.nombre}
                      </Text>
                      {!isDone && (
                        <Text
                          tid="recepciones.progreso.validando"
                          fontSize="xs"
                          color={colors.textMuted}
                        />
                      )}
                      {isDone && validation.result && (
                        <Text fontSize="xs" color={iconColor} fontWeight="500">
                          {validation.result.mensaje}
                        </Text>
                      )}
                      {hasError && (
                        <Text fontSize="xs" color="#c8442f" fontWeight="500">
                          {validation.error}
                        </Text>
                      )}
                    </Box>

                    {isDone && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                      >
                        <Box
                          px={2}
                          py={0.5}
                          borderRadius="full"
                          fontSize="10px"
                          fontWeight="700"
                          textTransform="uppercase"
                          bg={`${iconColor}20`}
                          color={iconColor}
                        >
                          {badgeLabel}
                        </Box>
                      </motion.div>
                    )}
                  </Flex>
                </Box>
              </motion.div>
            )
          })}
        </AnimatePresence>
      </VStack>

      {/* Summary when done */}
      {allDone && (
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          textAlign="center"
          py={4}
        >
          <Text
            tid="recepciones.progreso.completadas"
            fontSize="md"
            fontWeight="600"
            color={colors.text}
            mb={4}
          />
          <Box
            as="button"
            px={8}
            py={3}
            bg={brand.primary}
            color="white"
            borderRadius="lg"
            fontSize="sm"
            fontWeight="600"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            transition="all 0.2s"
            onClick={handleVerResultado}
          >
            {t('recepciones.actions.verResultado')}
          </Box>
        </MotionBox>
      )}
    </VStack>
  )
}
